// RemoteCursor — the "ghostly cursor" rendered in 3D space for each
// remote participant in a co-presence tour. Built as a small PlayCanvas
// entity (sphere + ring + canvas-baked label) on a dedicated layer
// above hotspots so it always reads over the panorama.
//
// Positioned at CURSOR_DISTANCE_M in front of the camera in the direction
// the remote participant is looking. Smoothed via per-frame lerp so the
// 10 Hz Pusher update rate doesn't jitter.

import * as pc from 'playcanvas';

const LAYER_NAME = 'remote-cursors';
const CURSOR_DISTANCE_M = 6.0;     // m from camera (panorama is at ~100; this sits in midfield)
const CURSOR_RADIUS_M = 0.18;
const LERP_RATE_PER_SEC = 8;        // higher = snappier, lower = smoother
const LABEL_CANVAS_W = 256;
const LABEL_CANVAS_H = 64;

const DEG2RAD = Math.PI / 180;

export class RemoteCursor {
  private readonly app: pc.AppBase;
  private readonly camera: pc.Entity;
  private readonly entity: pc.Entity;
  private readonly material: pc.StandardMaterial;
  private readonly labelEntity: pc.Entity;
  private readonly labelMaterial: pc.StandardMaterial;
  private readonly labelTexture: pc.Texture;
  private readonly labelCanvas: HTMLCanvasElement;
  private readonly layer: pc.Layer;
  private targetYawDeg = 0;
  private targetPitchDeg = 0;
  private displayedYawDeg = 0;
  private displayedPitchDeg = 0;
  private readonly tmpVec3 = new pc.Vec3();

  constructor(app: pc.AppBase, camera: pc.Entity, label: string, hue = 200) {
    this.app = app;
    this.camera = camera;

    // Dedicated layer above world+hotspots; depthTest off so it always paints on top.
    const existing = app.scene.layers.getLayerByName(LAYER_NAME);
    this.layer = existing ?? new pc.Layer({ name: LAYER_NAME });
    if (!existing) app.scene.layers.push(this.layer);
    if (camera.camera) {
      const camLayers = camera.camera.layers ?? [];
      if (!camLayers.includes(this.layer.id)) camera.camera.layers = [...camLayers, this.layer.id];
    }

    // Cursor body — small unlit sphere with a hue-rotated emissive.
    const color = hslColor(hue, 0.7, 0.55);
    this.material = new pc.StandardMaterial();
    this.material.useLighting = false;
    this.material.useFog = false;
    this.material.useTonemap = false;
    this.material.useSkybox = false;
    this.material.diffuse = new pc.Color(0, 0, 0);
    this.material.emissive = color;
    this.material.opacity = 0.92;
    this.material.blendType = pc.BLEND_NORMAL;
    this.material.depthWrite = false;
    this.material.depthTest = false;
    this.material.update();

    this.entity = new pc.Entity('remote-cursor');
    this.entity.addComponent('render', {
      type: 'sphere',
      material: this.material,
      layers: [this.layer.id],
      castShadows: false,
      receiveShadows: false,
    });
    this.entity.setLocalScale(CURSOR_RADIUS_M * 2, CURSOR_RADIUS_M * 2, CURSOR_RADIUS_M * 2);
    app.root.addChild(this.entity);

    // Label — a plane mesh above the sphere with the participant's name.
    this.labelCanvas = document.createElement('canvas');
    this.labelCanvas.width = LABEL_CANVAS_W;
    this.labelCanvas.height = LABEL_CANVAS_H;
    this.labelTexture = new pc.Texture(app.graphicsDevice, {
      width: LABEL_CANVAS_W,
      height: LABEL_CANVAS_H,
      format: pc.PIXELFORMAT_R8_G8_B8_A8,
      mipmaps: false,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });
    this.labelTexture.setSource(this.labelCanvas);
    this.paintLabel(label, color);
    this.labelTexture.upload();

    this.labelMaterial = new pc.StandardMaterial();
    this.labelMaterial.useLighting = false;
    this.labelMaterial.useFog = false;
    this.labelMaterial.useTonemap = false;
    this.labelMaterial.useSkybox = false;
    this.labelMaterial.diffuse = new pc.Color(0, 0, 0);
    this.labelMaterial.emissive = new pc.Color(1, 1, 1);
    this.labelMaterial.emissiveMap = this.labelTexture;
    this.labelMaterial.opacity = 0.95;
    this.labelMaterial.blendType = pc.BLEND_NORMAL;
    this.labelMaterial.depthWrite = false;
    this.labelMaterial.depthTest = false;
    this.labelMaterial.update();

    this.labelEntity = new pc.Entity('remote-cursor-label');
    this.labelEntity.addComponent('render', {
      type: 'plane',
      material: this.labelMaterial,
      layers: [this.layer.id],
      castShadows: false,
      receiveShadows: false,
    });
    this.labelEntity.setLocalScale(0.8, 1, 0.2);
    this.labelEntity.setLocalEulerAngles(-90, 0, 0);
    app.root.addChild(this.labelEntity);

    app.on('update', this.tick);
  }

  /** Set the remote look angles in degrees. */
  setLook(yawDeg: number, pitchDeg: number): void {
    this.targetYawDeg = yawDeg;
    this.targetPitchDeg = pitchDeg;
  }

  destroy(): void {
    this.app.off('update', this.tick);
    this.entity.destroy();
    this.labelEntity.destroy();
    this.material.destroy();
    this.labelMaterial.destroy();
    this.labelTexture.destroy();
  }

  // ─────────────────────────────────────────────────────────────────

  private readonly tick = (dtSec: number): void => {
    // Lerp displayed → target to smooth the 10 Hz network samples.
    const k = Math.min(1, dtSec * LERP_RATE_PER_SEC);
    this.displayedYawDeg = lerpDeg(this.displayedYawDeg, this.targetYawDeg, k);
    this.displayedPitchDeg = this.displayedPitchDeg + (this.targetPitchDeg - this.displayedPitchDeg) * k;

    // Project (yaw, pitch) → 3D direction. Matches the canonical
    // yawPitchToVec3() in mathUtils.ts: PlayCanvas's camera "forward" is its
    // local -Z, and a positive Y-axis rotation sends that forward to
    // (-sin yaw, 0, -cos yaw) in world space. Without the X-axis negation
    // the cursor lands on the opposite horizontal side of the panorama from
    // where the remote peer is actually looking — verified failure mode
    // 2026-05-18 once private-channel events started flowing for the first
    // time and the misalignment became observable end-to-end.
    const yawRad = this.displayedYawDeg * DEG2RAD;
    const pitchRad = this.displayedPitchDeg * DEG2RAD;
    const cp = Math.cos(pitchRad);
    const dirX = -Math.sin(yawRad) * cp;
    const dirY = Math.sin(pitchRad);
    const dirZ = -Math.cos(yawRad) * cp; // -Z is forward in PlayCanvas

    const camPos = this.camera.getPosition();
    this.tmpVec3.set(
      camPos.x + dirX * CURSOR_DISTANCE_M,
      camPos.y + dirY * CURSOR_DISTANCE_M,
      camPos.z + dirZ * CURSOR_DISTANCE_M,
    );
    this.entity.setPosition(this.tmpVec3);

    // Label sits a bit above the cursor + billboards to camera.
    this.tmpVec3.set(this.tmpVec3.x, this.tmpVec3.y + CURSOR_RADIUS_M * 2.5, this.tmpVec3.z);
    this.labelEntity.setPosition(this.tmpVec3);
    this.labelEntity.lookAt(camPos);
    this.labelEntity.rotateLocal(90, 0, 0);
  };

  private paintLabel(label: string, color: pc.Color): void {
    const ctx = this.labelCanvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, LABEL_CANVAS_W, LABEL_CANVAS_H);
    // Pill background.
    ctx.fillStyle = `rgba(0, 0, 0, 0.78)`;
    roundRect(ctx, 8, 8, LABEL_CANVAS_W - 16, LABEL_CANVAS_H - 16, 18);
    // Coloured dot.
    ctx.fillStyle = `rgb(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)})`;
    ctx.beginPath();
    ctx.arc(28, LABEL_CANVAS_H / 2, 8, 0, Math.PI * 2);
    ctx.fill();
    // Name.
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 22px system-ui, -apple-system, sans-serif';
    ctx.textBaseline = 'middle';
    ctx.fillText(label.slice(0, 18), 48, LABEL_CANVAS_H / 2);
  }
}

function lerpDeg(a: number, b: number, t: number): number {
  let diff = b - a;
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return a + diff * t;
}

function hslColor(h: number, s: number, l: number): pc.Color {
  // h: 0..360, s/l: 0..1. Tiny HSL→RGB so we don't pull in a util library.
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  return new pc.Color(r + m, g + m, b + m);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}
